import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { ReactComponent as FileUploadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import BomUnitsTabs from 'components/AICreatorBoMUnits/components/BomUnitsTabs/BomUnitsTabs';
import AICreatorSupplierUploadModal from 'components/AICreatorSupplierUploadModal/AICreatorSupplierUploadModal';
import AiSubScreen from 'components/AiSubScreen/AiSubScreen';
import Button from 'components/Button/Button';
import Backdrop from 'components/Modal/Backdrop';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import {
  DEFAULT_SOCKET_FALLBACK_DELAY,
  VALUE_CHAIN_AI_AGENT,
} from 'constants/general';
import { BUTTON_VARIANT, DELETE_MODAL_TITLES } from 'constants/interfaces';
import useAgentSessionSupplierDiscoveryMutations from 'hooks/useAgentSessionSupplierDiscoveryMutations';
import useDropdownToggle from 'hooks/useDropdownToggle';
import useReviewTransitionLoading from 'hooks/useReviewTransitionLoading';
import useSessionUnitsPollingState from 'hooks/useSessionUnitsPollingState';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';
import { useExploreAgentSessionSupplierDiscoveryAllTabsAiMutation } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import {
  getSupplierItemsNotFilled,
  getUnitIdFromNodeId,
  isAIAgentSessionReviewReadyStatus,
  isVcAgentBomSocketProcessingStatus,
  normalizeTabStatus,
} from 'utils/AICreatorHelper';
import { showBadRequestError } from 'utils/helpers';
import { getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import SupplierUnitsTable from './components/SupplierUnitsTable/SupplierUnitsTable';
import useAICreatorSupplierRoot from './hooks/useAICreatorSupplierRoot';
import useAICreatorSupplierTable from './hooks/useAICreatorSupplierTable';
import useAICreatorSupplierTabs from './hooks/useAICreatorSupplierTabs';

import type { IVCAgentSessionUnitUpdatedPayload } from '../../hooks/interfaces/IUseWebSocket';
import type { ISupplierFocusCell } from './components/RowAICreatorSupplier/interfaces/IRowAICreatorSupplier';
import type IAICreatorSupplierUnits from './interfaces/IAICreatorSupplierUnits';

import styles from './sass/AICreatorSupplierUnits.module.scss';

const SUPPLIER_AI_PENDING_STATUS_SETTLE_DELAY_MS = 1000;

type TSupplierSocketPayloadMeta = {
  key: string,
  receivedAt: number,
};

function AICreatorSupplierUnits({
  open,
  selectedCards,
  session,
  onClose,
  onGenerateWithAI,
  onAddRecord,
  onConfirm,
  initialActiveTabNodeId,
  handleCloseModalSupplier,
  onProcessingTabsChange,
  isSupplierSocketConnected,
  sendSupplierSocketUnitUpdated,
  lastSupplierSocketPayload,
  isSocketPollingFallbackActive,
  agentSessionsFulfilledTimeStamp,
  refetchAgentSessions,
}: IAICreatorSupplierUnits) {
  const { t } = useTranslation();
  const [supplierErrorFocusCell, setSupplierErrorFocusCell] = useState<ISupplierFocusCell | null>(null);
  const [focusPendingTabNodeId, setFocusPendingTabNodeId] = useState<string | null>(null);
  const [supplierRowPendingDeleteId, setSupplierRowPendingDeleteId] = useState<number | null>(null);
  const [supplierAiSocketPayload, setSupplierAiSocketPayload] = useState<IVCAgentSessionUnitUpdatedPayload>();
  const [lastSupplierSocketPayloadMeta, setLastSupplierSocketPayloadMeta] = useState<TSupplierSocketPayloadMeta | null>(
    null,
  );
  const [pendingAiUnitIds, setPendingAiUnitIds] = useState<number[]>([]);
  const [isConfirmAllLoading, setIsConfirmAllLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLocalSocketFallbackActive, setIsLocalSocketFallbackActive] = useState(false);
  const prevCurrentUnitIdRef = useRef<number | null>(null);
  const prevPollingSelectedUnitsKeyRef = useRef<string>('');
  const sentSupplierAiSocketPayloadRef = useRef<string>('');
  const handledSupplierSocketPayloadRef = useRef<string>('');
  const pendingAiUnitStartedAtByUnitIdRef = useRef<Map<number, number>>(new Map());
  const pendingAiFallbackTimerRef = useRef<number | null>(null);
  const wasPendingAiUnitIdsEmptyRef = useRef(true);
  const confirmErrorToastIdRef = useRef<ReturnType<typeof toast.error> | null>(null);

  const {
    isReviewTransitionLoading,
    startReviewTransitionLoading,
    clearReviewTransitionLoading,
    finishReviewTransitionLoading,
  } = useReviewTransitionLoading();

  const {
    isOpen: isGenerateDropdownOpen,
    ref: generateDropdownRef,
    toggle: toggleGenerateDropdown,
    close: closeGenerateDropdown,
  } = useDropdownToggle();

  const [exploreAgentSessionSupplierDiscoveryAllTabsAi] = useExploreAgentSessionSupplierDiscoveryAllTabsAiMutation();

  const {
    valueChainMapId,
    naceCodesData,
    isAgentSessionSupplierDiscoveryFetching,
    refetchAgentSessionSupplierDiscovery,
    agentSessionSupplierDiscoveryFulfilledTimeStamp,
    sessionUnitStatusByUnitId,
    updateAgentSessionSupplierDiscovery,
    startAgentSessionReview,
    isStartAgentSessionReviewLoading,
  } = useAICreatorSupplierRoot({ session });

  const {
    tabs,
    setTabs,
    activeTabNodeId,
    setActiveTabNodeId,
    isSelectAllChecked,
    setIsSelectAllChecked,
    activeTab,
    activeTabIndex,
    setSupplierUnitStatusOverride,
    syncCurrentSupplierStatusInProgress,
  } = useAICreatorSupplierTabs({
    open,
    selectedCards,
    session,
    sessionUnitStatusByUnitId,
    valueChainMapId,
    sessionId: session?.id,
    updateAgentSessionSupplierDiscovery,
    pendingAiUnitIds,
    initialActiveTabNodeId,
    onBadRequestError: showBadRequestError,
  });

  const currentUnitId = useMemo(() => (
    activeTab ? getUnitIdFromNodeId(activeTab.nodeId) : null
  ), [activeTab]);

  const {
    tableItems,
    setTableItemsForContext,
    onLoadMore,
    hasMore,
    isAgentSessionSupplierItemsFetching,
    refetchAgentSessionSupplierItems,
    markTableItemsContextStale,
  } = useAICreatorSupplierTable({
    valueChainMapId,
    sessionId: session?.id,
    unitId: currentUnitId,
    listContextKey: activeTabNodeId,
    open,
  });

  const {
    handleCreateRecord,
    handleUpdateRow,
    handleDeleteRow,
    handleSelectAllChange,
    handleGenerateWithAI: handleGenerateWithAIRequest,
    countryOptions,
    isDeleteAgentSessionSupplierDiscoveryItemLoading,
    isUpdateAgentSessionSupplierDiscoverySelectionLoading,
  } = useAgentSessionSupplierDiscoveryMutations({
    valueChainMapId,
    session,
    currentUnitId,
    tableContextKey: activeTabNodeId,
    tableItems,
    setTableItemsForContext,
    setIsSelectAllChecked,
    setPendingAiUnitIds,
    setSupplierAiSocketPayload,
    syncCurrentSupplierStatusInProgress,
    onAddRecord,
    onGenerateWithAI,
    onBadRequestError: showBadRequestError,
  });

  const naceOptions = naceCodesData;

  const {
    pollingSelectedUnitsKey,
    hasPollingSessionProcessingUnits,
    selectedUnitStatusByUnitId,
  } = useSessionUnitsPollingState(session?.selectedUnits);

  const supplierTableContextKeyByUnitId = useMemo(() => {
    const contextKeyByUnitId = new Map<number, string>();

    tabs.forEach((tab) => {
      const tabUnitId = getUnitIdFromNodeId(tab.nodeId);

      if (tabUnitId !== null) {
        contextKeyByUnitId.set(tabUnitId, tab.nodeId);
      }
    });

    return contextKeyByUnitId;
  }, [tabs]);
  const processingSupplierUnitIds = useMemo(() => {
    const unitIds = new Set<number>(pendingAiUnitIds);
    const supplierSocketPayload = lastSupplierSocketPayload;

    (session?.selectedUnits || []).forEach((selectedUnit) => {
      if (isVcAgentBomSocketProcessingStatus(selectedUnit.status)) {
        unitIds.add(selectedUnit.unitId);
      }
    });

    if (supplierSocketPayload && supplierSocketPayload.sessionId === session?.id) {
      if (isVcAgentBomSocketProcessingStatus(supplierSocketPayload.status)) {
        const socketPayloadKey = JSON.stringify(supplierSocketPayload);
        const socketReceivedAt = lastSupplierSocketPayloadMeta?.key === socketPayloadKey
          ? lastSupplierSocketPayloadMeta.receivedAt
          : undefined;
        const discoveryStatus = sessionUnitStatusByUnitId.get(supplierSocketPayload.unitId);
        const hasFreshDiscoveryStatusAfterSocket = Boolean(
          socketReceivedAt
          && agentSessionSupplierDiscoveryFulfilledTimeStamp
          && agentSessionSupplierDiscoveryFulfilledTimeStamp >= socketReceivedAt
          && Date.now() - socketReceivedAt >= SUPPLIER_AI_PENDING_STATUS_SETTLE_DELAY_MS
          && discoveryStatus
          && !isVcAgentBomSocketProcessingStatus(discoveryStatus),
        );

        if (!hasFreshDiscoveryStatusAfterSocket) {
          unitIds.add(supplierSocketPayload.unitId);
        }
      } else {
        unitIds.delete(supplierSocketPayload.unitId);
      }
    }

    return unitIds;
  }, [
    agentSessionSupplierDiscoveryFulfilledTimeStamp,
    lastSupplierSocketPayload,
    lastSupplierSocketPayloadMeta,
    pendingAiUnitIds,
    session?.id,
    session?.selectedUnits,
    sessionUnitStatusByUnitId,
  ]);
  const isSupplierTabProcessing = useMemo(() => {
    if (activeTab?.status === VALUE_CHAIN_AI_AGENT.AI_STATUS) {
      return true;
    }

    if (currentUnitId === null) {
      return false;
    }

    return processingSupplierUnitIds.has(currentUnitId);
  }, [activeTab?.status, currentUnitId, processingSupplierUnitIds]);
  const emptyStateMessage = useMemo(() => {
    const socketMessage = lastSupplierSocketPayload?.message?.trim();
    const socketUnitId = lastSupplierSocketPayload?.unitId;

    if (
      socketMessage
      && activeTab?.name
      && currentUnitId !== null
      && socketUnitId === currentUnitId
    ) {
      return t('valueChainMap.aiGeneratingNothingFound', {
        name: activeTab.name,
      });
    }

    return t('valueChainMap.supplierUnitsEmptyState', {
      name: activeTab?.name || '',
    });
  }, [
    activeTab?.name,
    currentUnitId,
    lastSupplierSocketPayload?.message,
    lastSupplierSocketPayload?.unitId,
    t,
  ]);

  const loading = isAgentSessionSupplierDiscoveryFetching
    || isAgentSessionSupplierItemsFetching
    || isStartAgentSessionReviewLoading;

  const areAllTabsCompleted = useMemo(
    () => tabs.length > 0 && tabs.every((tab) => tab.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT),
    [tabs],
  );
  const isLastTab = activeTabIndex >= 0 && activeTabIndex === tabs.length - 1;
  const isCreateAction = isLastTab && areAllTabsCompleted;
  const isSessionReviewReady = isAIAgentSessionReviewReadyStatus(session?.status);
  const isReviewAction = isCreateAction || isSessionReviewReady;
  const isFooterActionDisabled = isSessionReviewReady
    ? isAgentSessionSupplierDiscoveryFetching || !tabs.length
    : isAgentSessionSupplierDiscoveryFetching
      || !tabs.length
      || tableItems.length === 0
      || isSupplierTabProcessing;

  const clearPendingSupplierAiUnits = useCallback((unitIds: number[]) => {
    if (unitIds.length === 0) {
      return;
    }

    const completedUnitIds = new Set(unitIds);

    unitIds.forEach((unitId) => {
      pendingAiUnitStartedAtByUnitIdRef.current.delete(unitId);
    });

    setPendingAiUnitIds((prev) => prev.filter((unitId) => !completedUnitIds.has(unitId)));

    if (supplierAiSocketPayload && completedUnitIds.has(supplierAiSocketPayload.unitId)) {
      setSupplierAiSocketPayload(undefined);
      sentSupplierAiSocketPayloadRef.current = '';
    }
  }, [supplierAiSocketPayload]);

  const markSupplierUnitTablesStale = useCallback((unitIds: number[]) => {
    unitIds.forEach((unitId) => {
      const contextKey = supplierTableContextKeyByUnitId.get(unitId)
        ?? (currentUnitId === unitId ? activeTabNodeId : '');

      if (!contextKey) {
        return;
      }

      markTableItemsContextStale(unitId, contextKey);
    });
  }, [
    activeTabNodeId,
    currentUnitId,
    markTableItemsContextStale,
    supplierTableContextKeyByUnitId,
  ]);

  const tabsForIndicator = useMemo(() => {
    if (processingSupplierUnitIds.size === 0) {
      return tabs;
    }

    return tabs.map((tab) => {
      const tabUnitId = getUnitIdFromNodeId(tab.nodeId);

      if (tabUnitId !== null && processingSupplierUnitIds.has(tabUnitId)) {
        return tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS
          ? tab
          : { ...tab, status: VALUE_CHAIN_AI_AGENT.AI_STATUS };
      }

      return tab;
    });
  }, [processingSupplierUnitIds, tabs]);

  const isAnyTabAiProcessing = useMemo(
    () => tabs.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || tabsForIndicator.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || processingSupplierUnitIds.size > 0
      || pendingAiUnitIds.length > 0,
    [pendingAiUnitIds.length, processingSupplierUnitIds.size, tabs, tabsForIndicator],
  );

  const isConfirmAllDisabled = isFooterActionDisabled || isConfirmAllLoading || isAnyTabAiProcessing;

  useEffect(() => {
    if (!lastSupplierSocketPayload) {
      setLastSupplierSocketPayloadMeta(null);
      return;
    }

    setLastSupplierSocketPayloadMeta({
      key: JSON.stringify(lastSupplierSocketPayload),
      receivedAt: Date.now(),
    });
  }, [lastSupplierSocketPayload]);

  useEffect(() => {
    const pendingSet = new Set(pendingAiUnitIds);

    pendingAiUnitIds.forEach((unitId) => {
      if (pendingAiUnitStartedAtByUnitIdRef.current.has(unitId)) {
        return;
      }

      pendingAiUnitStartedAtByUnitIdRef.current.set(unitId, Date.now());
    });

    Array.from(pendingAiUnitStartedAtByUnitIdRef.current.keys()).forEach((unitId) => {
      if (!pendingSet.has(unitId)) {
        pendingAiUnitStartedAtByUnitIdRef.current.delete(unitId);
      }
    });
  }, [pendingAiUnitIds]);

  useEffect(() => {
    if (!lastSupplierSocketPayload || !session?.id || lastSupplierSocketPayload.sessionId !== session.id) {
      return;
    }

    const matchedTabUnitId = lastSupplierSocketPayload.unitId;
    const isSocketProcessing = isVcAgentBomSocketProcessingStatus(lastSupplierSocketPayload.status);
    const socketStatus = normalizeTabStatus(lastSupplierSocketPayload.status);
    const normalizedSocketStatus = socketStatus === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT
      ? VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
      : socketStatus;
    const payloadKey = JSON.stringify(lastSupplierSocketPayload);

    if (!isSocketProcessing) {
      setSupplierUnitStatusOverride(matchedTabUnitId, normalizedSocketStatus);
    }

    setTabs((prevTabs) => {
      let hasStatusChanged = false;

      const nextTabs = prevTabs.map((tab) => {
        const tabUnitId = getUnitIdFromNodeId(tab.nodeId);

        if (tabUnitId !== matchedTabUnitId) {
          return tab;
        }

        if (tab.status === normalizedSocketStatus) {
          return tab;
        }

        hasStatusChanged = true;

        return {
          ...tab,
          status: normalizedSocketStatus,
        };
      });

      return hasStatusChanged ? nextTabs : prevTabs;
    });

    if (isSocketProcessing || handledSupplierSocketPayloadRef.current === payloadKey) {
      return;
    }

    handledSupplierSocketPayloadRef.current = payloadKey;
    markSupplierUnitTablesStale([matchedTabUnitId]);
    clearPendingSupplierAiUnits([matchedTabUnitId]);
    Promise.resolve(refetchAgentSessionSupplierDiscovery()).catch(() => {});

    if (open && currentUnitId !== null && currentUnitId === matchedTabUnitId) {
      refetchAgentSessionSupplierItems();
    }
  }, [
    currentUnitId,
    clearPendingSupplierAiUnits,
    lastSupplierSocketPayload,
    markSupplierUnitTablesStale,
    open,
    refetchAgentSessionSupplierDiscovery,
    refetchAgentSessionSupplierItems,
    session?.id,
    setTabs,
    setSupplierUnitStatusOverride,
  ]);

  useEffect(() => {
    if (!onProcessingTabsChange) {
      return;
    }

    if (!open) {
      onProcessingTabsChange(false);
      return;
    }

    const hasProcessingTabs = tabs.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || processingSupplierUnitIds.size > 0;

    onProcessingTabsChange(hasProcessingTabs);
  }, [onProcessingTabsChange, open, processingSupplierUnitIds.size, tabs]);

  useEffect(() => {
    if (
      !open
      || pendingAiUnitIds.length === 0
      || !agentSessionsFulfilledTimeStamp
    ) {
      return;
    }

    const completedPendingUnitIds = pendingAiUnitIds.filter((unitId) => {
      const startedAt = pendingAiUnitStartedAtByUnitIdRef.current.get(unitId);
      const liveStatus = sessionUnitStatusByUnitId.get(unitId) ?? selectedUnitStatusByUnitId.get(unitId);

      return Boolean(
        startedAt
        && agentSessionsFulfilledTimeStamp >= startedAt
        && liveStatus
        && !isVcAgentBomSocketProcessingStatus(liveStatus),
      );
    });

    if (completedPendingUnitIds.length === 0) {
      return;
    }

    markSupplierUnitTablesStale(completedPendingUnitIds);
    clearPendingSupplierAiUnits(completedPendingUnitIds);
    Promise.resolve(refetchAgentSessionSupplierDiscovery()).catch(() => {});

    if (currentUnitId !== null && completedPendingUnitIds.includes(currentUnitId)) {
      refetchAgentSessionSupplierItems();
    }
  }, [
    agentSessionsFulfilledTimeStamp,
    clearPendingSupplierAiUnits,
    currentUnitId,
    markSupplierUnitTablesStale,
    open,
    pendingAiUnitIds,
    refetchAgentSessionSupplierDiscovery,
    refetchAgentSessionSupplierItems,
    selectedUnitStatusByUnitId,
    sessionUnitStatusByUnitId,
  ]);

  useEffect(() => {
    const prevKey = prevPollingSelectedUnitsKeyRef.current;
    prevPollingSelectedUnitsKeyRef.current = pollingSelectedUnitsKey;

    if (
      !open
      || (!isSocketPollingFallbackActive && !isLocalSocketFallbackActive)
      || pendingAiUnitIds.length === 0
      || !session?.selectedUnits?.length
      || !prevKey
      || prevKey === pollingSelectedUnitsKey
    ) {
      return;
    }

    const pollingSelectedUnitStatusByUnitId = new Map(
      session.selectedUnits.map((selectedUnit) => [selectedUnit.unitId, selectedUnit.status] as const),
    );
    const completedPendingUnitIds = pendingAiUnitIds.filter((unitId) => {
      const liveStatus = sessionUnitStatusByUnitId.get(unitId) ?? pollingSelectedUnitStatusByUnitId.get(unitId);

      return Boolean(liveStatus) && !isVcAgentBomSocketProcessingStatus(liveStatus);
    });

    if (completedPendingUnitIds.length === 0) {
      return;
    }

    markSupplierUnitTablesStale(completedPendingUnitIds);
    clearPendingSupplierAiUnits(completedPendingUnitIds);
    Promise.resolve(refetchAgentSessionSupplierDiscovery()).catch(() => {});

    if (
      currentUnitId !== null
      && (
        !hasPollingSessionProcessingUnits
        || completedPendingUnitIds.includes(currentUnitId)
      )
    ) {
      refetchAgentSessionSupplierItems();
    }
  }, [
    clearPendingSupplierAiUnits,
    currentUnitId,
    hasPollingSessionProcessingUnits,
    isLocalSocketFallbackActive,
    isSocketPollingFallbackActive,
    markSupplierUnitTablesStale,
    open,
    pendingAiUnitIds,
    pollingSelectedUnitsKey,
    refetchAgentSessionSupplierDiscovery,
    refetchAgentSessionSupplierItems,
    session?.selectedUnits,
    sessionUnitStatusByUnitId,
  ]);

  useEffect(() => {
    if (pendingAiUnitIds.length === 0) {
      if (pendingAiFallbackTimerRef.current !== null) {
        window.clearTimeout(pendingAiFallbackTimerRef.current);
        pendingAiFallbackTimerRef.current = null;
      }

      wasPendingAiUnitIdsEmptyRef.current = true;
      setIsLocalSocketFallbackActive(false);
      return;
    }

    if (!wasPendingAiUnitIdsEmptyRef.current) {
      return;
    }

    wasPendingAiUnitIdsEmptyRef.current = false;

    pendingAiFallbackTimerRef.current = window.setTimeout(() => {
      pendingAiFallbackTimerRef.current = null;
      setIsLocalSocketFallbackActive(true);
    }, DEFAULT_SOCKET_FALLBACK_DELAY);
  }, [pendingAiUnitIds]);

  useEffect(() => () => {
    if (pendingAiFallbackTimerRef.current !== null) {
      window.clearTimeout(pendingAiFallbackTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const hasProcessingTabs = tabs.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || hasPollingSessionProcessingUnits
      || pendingAiUnitIds.length > 0
      || processingSupplierUnitIds.size > 0;

    if (!open || (!isSocketPollingFallbackActive && !isLocalSocketFallbackActive) || !hasProcessingTabs) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      Promise.resolve(refetchAgentSessions()).catch(() => {});
      refetchAgentSessionSupplierDiscovery();

      if (currentUnitId !== null) {
        refetchAgentSessionSupplierItems();
      }
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    currentUnitId,
    hasPollingSessionProcessingUnits,
    isLocalSocketFallbackActive,
    isSocketPollingFallbackActive,
    open,
    pendingAiUnitIds.length,
    processingSupplierUnitIds.size,
    refetchAgentSessions,
    refetchAgentSessionSupplierDiscovery,
    refetchAgentSessionSupplierItems,
    tabs,
  ]);

  useEffect(() => {
    const isUnitIdStable = prevCurrentUnitIdRef.current === currentUnitId;
    prevCurrentUnitIdRef.current = currentUnitId;

    if (!isUnitIdStable) {
      return;
    }

    if (!focusPendingTabNodeId || activeTab?.nodeId !== focusPendingTabNodeId) {
      return;
    }

    if (isAgentSessionSupplierItemsFetching) {
      return;
    }

    const firstNotFilled = getSupplierItemsNotFilled(tableItems)[0];

    if (firstNotFilled) {
      setSupplierErrorFocusCell({
        ...firstNotFilled,
        focusKey: Date.now(),
      });
    }

    setFocusPendingTabNodeId(null);
  }, [
    activeTab?.nodeId,
    currentUnitId,
    focusPendingTabNodeId,
    isAgentSessionSupplierItemsFetching,
    tableItems,
  ]);

  useEffect(() => {
    if (!open) {
      setSupplierAiSocketPayload(undefined);
      sentSupplierAiSocketPayloadRef.current = '';
      handledSupplierSocketPayloadRef.current = '';
      prevPollingSelectedUnitsKeyRef.current = '';
      setPendingAiUnitIds([]);
      clearReviewTransitionLoading();
      confirmErrorToastIdRef.current = null;
    }
  }, [clearReviewTransitionLoading, open]);

  useEffect(() => {
    if (!supplierAiSocketPayload) {
      sentSupplierAiSocketPayloadRef.current = '';
      return;
    }

    if (!isSupplierSocketConnected) {
      return;
    }

    const payloadKey = JSON.stringify(supplierAiSocketPayload);
    if (sentSupplierAiSocketPayloadRef.current === payloadKey) {
      return;
    }

    const isSent = sendSupplierSocketUnitUpdated(supplierAiSocketPayload);
    if (isSent) {
      sentSupplierAiSocketPayloadRef.current = payloadKey;
    }
  }, [isSupplierSocketConnected, sendSupplierSocketUnitUpdated, supplierAiSocketPayload]);

  useEffect(() => {
    setIsSelectAllChecked(tableItems.some((tableItem) => tableItem.isActive));
  }, [setIsSelectAllChecked, tableItems]);

  const handleRequestDeleteRow = (itemId: number) => {
    setSupplierRowPendingDeleteId(itemId);
  };

  const handleRejectDeleteRow = () => {
    setSupplierRowPendingDeleteId(null);
  };

  const handleConfirmDeleteRow = async () => {
    if (supplierRowPendingDeleteId == null) {
      return;
    }

    const itemId = supplierRowPendingDeleteId;

    try {
      await handleDeleteRow(itemId);
      setSupplierRowPendingDeleteId(null);
    } catch {
      // Keep modal open so the user can retry after a failed request.
    }
  };

  const handleClose = () => {
    setIsUploadModalOpen(false);
    onClose();
  };

  const handleUploadModalOpen = useCallback(() => {
    if (currentUnitId === null || isSupplierTabProcessing) {
      return;
    }

    setIsUploadModalOpen(true);
  }, [currentUnitId, isSupplierTabProcessing]);

  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleUploadImported = useCallback(() => {
    setSupplierErrorFocusCell(null);
    refetchAgentSessionSupplierDiscovery();
    refetchAgentSessionSupplierItems();
  }, [refetchAgentSessionSupplierDiscovery, refetchAgentSessionSupplierItems]);

  const handleGenerateWithAI = async () => {
    if (currentUnitId !== null && activeTabNodeId) {
      markTableItemsContextStale(currentUnitId, activeTabNodeId);
    }

    await handleGenerateWithAIRequest();
  };

  const handleGenerateWithAIAllTabs = async () => {
    if (!valueChainMapId || !session?.id) {
      return;
    }

    closeGenerateDropdown();

    const allUnitIds = tabs
      .map((tab) => getUnitIdFromNodeId(tab.nodeId))
      .filter((unitId): unitId is number => unitId !== null);

    setPendingAiUnitIds((prev) => {
      const pendingSet = new Set(prev);
      allUnitIds.forEach((id) => pendingSet.add(id));
      return Array.from(pendingSet);
    });

    try {
      await exploreAgentSessionSupplierDiscoveryAllTabsAi({
        valueChainMapId,
        sessionId: session.id,
      }).unwrap();
      await new Promise<void>((resolve) => { window.setTimeout(resolve, 500); });
      Promise.resolve(refetchAgentSessionSupplierDiscovery()).catch(() => {});
      onGenerateWithAI();
    } catch (error) {
      setPendingAiUnitIds((prev) => prev.filter((id) => !allUnitIds.includes(id)));
      showBadRequestError(error);
    }
  };

  const handleStartReview = async (): Promise<boolean> => {
    if (!valueChainMapId || !session?.id) {
      return false;
    }

    try {
      await startAgentSessionReview({
        valueChainMapId,
        sessionId: session.id,
      }).unwrap();
      onConfirm();
      handleCloseModalSupplier();

      return true;
    } catch (error) {
      confirmErrorToastIdRef.current = showBadRequestError(error)
        ?? toast.error(t('notifications.somethingWentWrong'));

      return false;
    }
  };

  const handleConfirmAll = async () => {
    if (!valueChainMapId || !session?.id || isAnyTabAiProcessing) {
      return;
    }

    if (isSessionReviewReady) {
      onConfirm();
      handleCloseModalSupplier();
      return;
    }

    setIsConfirmAllLoading(true);

    const incompleteTabs = tabs.filter(
      (tab) => tab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    );

    let hasError = false;

    await incompleteTabs.reduce(async (previousPromise, tab) => {
      await previousPromise;

      if (hasError) {
        return;
      }

      const unitId = getUnitIdFromNodeId(tab.nodeId);

      if (unitId === null) {
        return;
      }

      try {
        await updateAgentSessionSupplierDiscovery({
          valueChainMapId: valueChainMapId!,
          sessionId: session!.id,
          unitId,
          status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
        }).unwrap();
        setSupplierUnitStatusOverride(unitId, VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT);
      } catch (error) {
        showBadRequestError(error);
        hasError = true;
      }
    }, Promise.resolve());

    if (hasError) {
      setIsConfirmAllLoading(false);
      return;
    }

    setTabs((prev) => prev.map((tab) => ({
      ...tab,
      status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    })));

    await handleStartReview();
    setIsConfirmAllLoading(false);
  };

  const handleConfirm = async () => {
    if (!valueChainMapId || !session?.id) {
      return;
    }

    if (confirmErrorToastIdRef.current !== null) {
      toast.dismiss(confirmErrorToastIdRef.current);
      confirmErrorToastIdRef.current = null;
    }

    if (isSessionReviewReady) {
      onConfirm();
      handleCloseModalSupplier();
      return;
    }

    if (isCreateAction) {
      await handleStartReview();
      return;
    }

    if (currentUnitId === null || !activeTab) {
      return;
    }

    const notFilledSupplierRows = getSupplierItemsNotFilled(tableItems);

    if (notFilledSupplierRows.length) {
      const firstNotFilled = notFilledSupplierRows[0];

      setSupplierErrorFocusCell({
        ...firstNotFilled,
        focusKey: Date.now(),
      });
      toast.error(t('notifications.valueChainSupplierUnitsLinesNotFilled'));
      return;
    }

    setSupplierErrorFocusCell(null);

    const isCompletingActiveTab = activeTab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
    const nextTabsAfterConfirm = isCompletingActiveTab
      ? tabs.map((tab) => (
        tab.nodeId === activeTab.nodeId
          ? { ...tab, status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT }
          : tab
      ))
      : tabs;
    const firstIncompleteTab = nextTabsAfterConfirm.find(
      (tab) => tab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    );
    const nextTab = firstIncompleteTab ? undefined : nextTabsAfterConfirm[activeTabIndex + 1];
    const nextActiveTabIndex = nextTab ? activeTabIndex + 1 : activeTabIndex;
    const shouldDelayReviewTransition = !firstIncompleteTab
      && !isCompletingActiveTab
      && nextTabsAfterConfirm.length > 0
      && nextActiveTabIndex === nextTabsAfterConfirm.length - 1
      && nextTabsAfterConfirm.every(
        (tab) => tab.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
      );
    const reviewTransitionStartedAt = shouldDelayReviewTransition
      ? startReviewTransitionLoading()
      : null;

    if (isCompletingActiveTab) {
      try {
        await updateAgentSessionSupplierDiscovery({
          valueChainMapId,
          sessionId: session.id,
          unitId: currentUnitId,
          status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
        }).unwrap();
      } catch (error) {
        clearReviewTransitionLoading();
        confirmErrorToastIdRef.current = showBadRequestError(error)
          ?? toast.error(t('notifications.somethingWentWrong'));
        return;
      }
      setSupplierUnitStatusOverride(currentUnitId, VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT);
    }

    if (firstIncompleteTab) {
      if (isCompletingActiveTab) {
        setTabs(nextTabsAfterConfirm);
      }

      setActiveTabNodeId(firstIncompleteTab.nodeId);
      setFocusPendingTabNodeId(firstIncompleteTab.nodeId);
      return;
    }

    if (isCompletingActiveTab) {
      setTabs(nextTabsAfterConfirm);
      await handleStartReview();
      return;
    }

    if (nextTab) {
      if (isCompletingActiveTab) {
        setTabs(nextTabsAfterConfirm);
      }

      await finishReviewTransitionLoading(reviewTransitionStartedAt);
      setActiveTabNodeId(nextTab.nodeId);
      return;
    }

    await finishReviewTransitionLoading(reviewTransitionStartedAt);

    if (isCompletingActiveTab) {
      setTabs(nextTabsAfterConfirm);
    }
  };

  if (!open) {
    return null;
  }

  const supplierModalTitle = (() => {
    const templateTitle = getSessionTemplateTitle(session);
    const baseTitle = t('valueChainMap.supplierUnitsModalTitle');

    return templateTitle ? `${baseTitle} · ${templateTitle}` : baseTitle;
  })();

  return createPortal(
    <>
      {/* Declared to the assistant under the title the modal shows. The tab goes in the data it
          sends, never in the composer: a session unit is (sessionId, unitId), and the agent
          session tools take a tab by its unitId. */}
      <AiSubScreen
        name={supplierModalTitle}
        customData={{
          sessionId: session?.id,
          subType: VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE,
          activeTab: currentUnitId === null ? null : { unitId: currentUnitId, name: activeTab?.name },
        }}
      />
      <Backdrop>
        <div className={styles.root}>
          <div className={styles.content}>
            <div className={styles.header}>
              <h2 className={styles.title}>{supplierModalTitle}</h2>
              <div className={styles.actions}>
                <div ref={generateDropdownRef} className={styles.generateWrapper}>
                  <Button
                    title={t('valueChainMap.bomUnitsGenerateWithAI')}
                    variant={BUTTON_VARIANT.primary}
                    startIcon={<AiIcon />}
                    className={styles.actionButton}
                    onClick={toggleGenerateDropdown}
                    disabled={isSupplierTabProcessing}
                  />
                  {isGenerateDropdownOpen ? (
                    <div className={styles.generateDropdown}>
                      <button
                        type="button"
                        className={styles.generateDropdownItem}
                        onClick={handleGenerateWithAIAllTabs}
                      >
                        {t('valueChainMap.bomUnitsGenerateWithAIForAllItems')}
                      </button>
                      <button
                        type="button"
                        className={styles.generateDropdownItem}
                        onClick={() => {
                          closeGenerateDropdown();
                          handleGenerateWithAI();
                        }}
                      >
                        {t('valueChainMap.bomUnitsGenerateWithAIForCurrentOnly')}
                      </button>
                    </div>
                  ) : null}
                </div>
                <Button
                  title={t('valueChainMap.bomUnitsUploadFile')}
                  variant={BUTTON_VARIANT.primary}
                  startIcon={<FileUploadIcon />}
                  className={styles.actionButton}
                  onClick={handleUploadModalOpen}
                  disabled={currentUnitId === null || isSupplierTabProcessing}
                />
                <Button
                  title={t('valueChainMap.bomUnitsAddRecord')}
                  variant={BUTTON_VARIANT.primary}
                  startIcon={<PlusIcon />}
                  className={styles.actionButton}
                  onClick={handleCreateRecord}
                  disabled={isSupplierTabProcessing}
                />
              </div>
            </div>
            <BomUnitsTabs
              open={open}
              tabs={tabsForIndicator}
              activeNodeId={activeTab?.nodeId ?? ''}
              onTabSelect={setActiveTabNodeId}
            />
            <SupplierUnitsTable
              open={open}
              isAnalysisInProgress={isSupplierTabProcessing}
              isSelectAllLoading={isUpdateAgentSessionSupplierDiscoverySelectionLoading}
              isSelectAllChecked={isSelectAllChecked}
              onSelectAllChange={handleSelectAllChange}
              tableItems={tableItems}
              naceOptions={naceOptions}
              countryOptions={countryOptions}
              onUpdate={handleUpdateRow}
              onDelete={handleRequestDeleteRow}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              emptyStateMessage={emptyStateMessage}
              focusCell={supplierErrorFocusCell ?? undefined}
              onStartEdit={() => setSupplierErrorFocusCell(null)}
            />
          </div>
          <div className={styles.footer}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              className={styles.footerButton}
              onClick={handleClose}
            />
            <div className={styles.footerConfirmGroup}>
              <Button
                title={t('common.confirmAll')}
                variant={BUTTON_VARIANT.cancel}
                className={styles.footerButton}
                onClick={handleConfirmAll}
                disabled={isConfirmAllDisabled}
                isLoading={isConfirmAllLoading}
                loadingIndicator={<SmallLoader size={18} />}
              />
              <Button
                title={isReviewAction ? t('common.review') : t('common.confirm')}
                variant={BUTTON_VARIANT.primary}
                className={styles.footerButton}
                onClick={handleConfirm}
                disabled={isFooterActionDisabled}
                isLoading={loading || isReviewTransitionLoading}
                loadingIndicator={<SmallLoader size={18} />}
              />
            </div>
          </div>
        </div>
      </Backdrop>
      <DeleteModal
        open={supplierRowPendingDeleteId != null}
        onReject={handleRejectDeleteRow}
        isLoading={isDeleteAgentSessionSupplierDiscoveryItemLoading}
        name=""
        title={DELETE_MODAL_TITLES.DELETE_CONFIRMATION}
        onConfirm={handleConfirmDeleteRow}
      />
      <AICreatorSupplierUploadModal
        open={isUploadModalOpen}
        valueChainMapId={valueChainMapId || ''}
        sessionId={session?.id}
        unitId={currentUnitId}
        onClose={handleUploadModalClose}
        onImported={handleUploadImported}
      />
    </>,
    document.body,
  );
}

export default AICreatorSupplierUnits;
